/**
 * Avviso NON bloccante: un altro cliente condivide lo stesso codice fiscale (V67).
 *
 * Dopo la V67 la condivisione è legittima — persona fisica e ditta individuale
 * sono lo stesso soggetto con due posizioni fiscali distinte. L'avviso serve a
 * intercettare il caso opposto: la ditta individuale che compila il form
 * dimenticando la P.IVA e finisce registrata come persona fisica. È corretto
 * fiscalmente, ma senza questo avviso resterebbe invisibile.
 *
 * Stessa forma di `AvvisiRiassegnazione` e `AvvisoBozzeAperte`: banda
 * informativa, elenco dei soggetti coinvolti, chiudibile.
 */
import { Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { identitaFiscale } from '../lib/identitaFiscale';

export interface ClienteOmonimo {
  id: string;
  ragione_sociale: string;
  piva: string | null;
}

interface Props {
  omonimi: ClienteOmonimo[];
  onDismiss?: () => void;
}

export default function AvvisoOmonimiaCf({ omonimi, onDismiss }: Props) {
  const { t } = useTranslation();
  if (omonimi.length === 0) return null;

  return (
    <div
      data-testid="avviso-omonimia-cf"
      className="flex items-start gap-2.5 px-4 py-3 rounded-lg border border-accent-info/30 bg-accent-info-bg text-sm text-ink-dark"
    >
      <Users size={16} className="text-accent-info flex-shrink-0 mt-0.5" />
      <div className="flex-1 space-y-1">
        <p>{t('anagrafiche.omonimia_cf.testo', { count: omonimi.length })}</p>
        <ul className="space-y-0.5">
          {omonimi.map((o) => (
            <li key={o.id} className="text-xs text-ink-soft">
              <a href={`/clienti/${o.id}`} className="text-sage hover:underline font-medium">
                {o.ragione_sociale}
              </a>
              {identitaFiscale(o) ? ` — ${identitaFiscale(o)}` : ''}
            </li>
          ))}
        </ul>
        <p className="text-xs text-ink-faint">{t('anagrafiche.omonimia_cf.nota')}</p>
      </div>
      {onDismiss && (
        <button type="button" onClick={onDismiss}
          className="text-xs text-ink-soft hover:text-ink-dark transition-colors flex-shrink-0">
          {t('common.azioni.chiudi')}
        </button>
      )}
    </div>
  );
}
